class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onCartReady = () => {
      if (this.classList.contains('active')) this.openSideCart();
    };
    this.onSideCartOpen = () => this.close(false);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay, [data-cart-continue]')) this.close();
    });
    this.setHeaderCartIconAccessibility();
    document.addEventListener('submit', (event) => this.handlePlainProductForm(event));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    // Section rendering replaces cart-link contents. Delegation also avoids
    // racing the cart app's direct link listener into two simultaneous drawers.
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('a[href], #cart-icon-bubble');
      if (!trigger) return;
      const url = new URL(trigger.href || '/', location.origin);
      if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== window.routes.cart_url.replace(/\/$/, '')) return;
      if (url.searchParams.has('checkout')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(trigger);
    }, true);
    document.addEventListener('keydown', (event) => {
      const trigger = event.target.closest('#cart-icon-bubble');
      if (trigger && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(trigger);
      }
    }, true);
  }

  async handlePlainProductForm(event) {
    // Existing AJAX product/quick-add handlers keep ownership of their forms.
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const url = new URL(form.action, location.origin);
    if (url.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(url.pathname)) return;
    event.preventDefault();
    if (form.dataset.dutAdding) return;
    form.dataset.dutAdding = 'true';
    const button = event.submitter || form.querySelector('[type="submit"]');
    const wasDisabled = button?.disabled;
    if (button) button.disabled = true;
    let error = form.querySelector('[data-dut-cart-error]');
    if (error) error.remove();
    try {
      const response = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST', body: new FormData(form),
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || 'Unable to add this item. Please try again.');
      this.open(button);
    } catch (failure) {
      error = document.createElement('p');
      error.dataset.dutCartError = '';
      error.setAttribute('role', 'alert');
      error.textContent = failure.message || 'Unable to add this item. Please try again.';
      form.append(error);
    } finally {
      delete form.dataset.dutAdding;
      if (button) button.disabled = wasDisabled;
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.openSideCart()) return;
    if (this.classList.contains('active')) return;
    if (this.parentElement !== document.body) document.body.append(this);
    // The installed app hides native drawers during setup, even when its own
    // panel subsequently fails to load. The fallback must remain available.
    this.style.removeProperty('display');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate', 'active');
    document.body.classList.add('dut-native-cart-open');
    window.addEventListener('dut:cart-ready', this.onCartReady);
    window.addEventListener('dut:cart-open', this.onSideCartOpen);
    this.focusWhenVisible();
    this.loadRecommendations();
    if (triggeredBy) this.refreshCart();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  openSideCart() {
    if (!window.DUTCartPanel?.open(this.activeElement)) return false;
    this.close(false);
    return true;
  }

  loadRecommendations() {
    if (!this.classList.contains('active')) return;
    const recommendations = this.querySelector('.dut-native-recommendations');
    if (!recommendations || recommendations.dataset.dutRequested || typeof recommendations.loadRecommendations !== 'function') return;
    // The cart scroll container clips IntersectionObserver's preload margin on
    // small screens. Start this existing component once when its cart opens.
    recommendations.dataset.dutRequested = 'true';
    recommendations.observer?.disconnect();
    recommendations.loadRecommendations(recommendations.dataset.productId);
  }

  focusWhenVisible() {
    this.cancelPendingFocus?.();
    let frameId;
    const cancel = () => {
      cancelAnimationFrame(frameId);
      this.removeEventListener('transitionend', focus);
      if (this.cancelPendingFocus === cancel) this.cancelPendingFocus = null;
    };
    const focus = () => {
      if (!this.classList.contains('active')) { cancel(); return; }
      const inner = this.querySelector('.drawer__inner');
      if (!inner || getComputedStyle(inner).visibility !== 'visible') return;
      const close = [...inner.querySelectorAll('.drawer__close')].find(button => button.getClientRects().length && getComputedStyle(button).visibility === 'visible');
      cancel();
      trapFocus(inner, inner.contains(document.activeElement) ? document.activeElement : close || inner);
    };
    this.cancelPendingFocus = cancel;
    this.addEventListener('transitionend', focus);
    frameId = requestAnimationFrame(focus);
  }

  close(restoreFocus = true) {
    const wasOpen = this.classList.contains('active');
    this.classList.remove('active');
    document.body.classList.remove('dut-native-cart-open');
    window.removeEventListener('dut:cart-ready', this.onCartReady);
    window.removeEventListener('dut:cart-open', this.onSideCartOpen);
    this.cancelPendingFocus?.();
    // Cancel any pending display/focus callback when the shopper dismisses.
    this.refreshVersion = (this.refreshVersion || 0) + 1;
    if (wasOpen) removeTrapFocus(restoreFocus && this.activeElement?.isConnected ? this.activeElement : null);
  }

  async refreshCart() {
    const version = this.refreshVersion = (this.refreshVersion || 0) + 1;
    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Cart unavailable');
      const html = await response.text();
      if (version !== this.refreshVersion || !this.classList.contains('active')) return;
      this.renderContents({ sections: { 'cart-drawer': html } }, false);
    } catch (_) {
      if (version !== this.refreshVersion || !this.classList.contains('active')) return;
      const error = this.querySelector('#CartDrawer-CartErrors');
      if (error) error.textContent = 'Your cart could not refresh. Please try opening it again.';
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, shouldOpen = true) {
    this.productId = parsedState.id;
    let incomplete = false;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const html = this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector);
      if (!sectionElement || html === null) {
        if (section.id === 'cart-drawer') incomplete = true;
        return;
      }
      sectionElement.innerHTML = html;
    });
    this.classList.toggle('is-empty', Boolean(this.querySelector('cart-drawer-items.is-empty')));
    if (shouldOpen) this.open();
    else if (this.classList.contains('active')) {
      this.focusWhenVisible();
      this.loadRecommendations();
    }
    if (incomplete && this.classList.contains('active')) {
      const error = this.querySelector('#CartDrawer-CartErrors');
      if (error) error.textContent = shouldOpen ? 'Refreshing your cart…' : 'Your cart could not refresh. Please try opening it again.';
      if (shouldOpen) this.refreshCart();
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Wait for the main cart before opening, avoiding a native-to-app drawer flash.
function openCartFromHandoff() {
  const url = new URL(location.href);
  if (url.searchParams.get('open_cart') !== '1') return;
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  const trigger = document.querySelector('#cart-icon-bubble');
  let finished = false;
  let fallbackTimer;
  const finish = () => {
    finished = true;
    clearTimeout(fallbackTimer);
    window.removeEventListener('dut:cart-ready', ready);
    url.searchParams.delete('open_cart');
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  };
  const ready = () => {
    if (finished || !window.DUTCartPanel?.open(trigger)) return;
    finish();
  };
  window.addEventListener('dut:cart-ready', ready);
  fallbackTimer = setTimeout(() => {
    if (finished) return;
    drawer.open(trigger);
    // If the app failed to load, keep the fallback rather than swapping later.
    window.removeEventListener('dut:cart-ready', drawer.onCartReady);
    finish();
  }, 8000);
  ready();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', openCartFromHandoff, { once: true });
} else {
  openCartFromHandoff();
}
